import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MessageCircle, Home, X, Flag, Shield, BarChart3, Users, FileText, AlertCircle, CheckCircle, Settings } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import Avatar from '../../components/common/Avatar';
import UserAdminView from '../../components/admin/UserAdminView';
import AppConfigView from '../../components/admin/AppConfigView';

const AdminPanel = () => {
  const { currentUser, view, setView, users, posts, setPosts, reports, setReports, disputes, setDisputes, suspendUser, unsuspendUser, unbanUser, wordFilters, setWordFilters, showAlert } = useContext(AppContext);
  const [disputeTab, setDisputeTab] = useState('pending');
  const [resolutionNotes, setResolutionNotes] = useState({});

  const [newBlockedWord, setNewBlockedWord] = useState('');
  const [newReplacement, setNewReplacement] = useState('');

  const handleAddWordFilter = async () => {
    if (!newBlockedWord.trim()) return;
    const updated = [...wordFilters, { blocked: newBlockedWord.trim().toLowerCase(), replacement: newReplacement.trim() || '***' }];
    setWordFilters(updated);
    setNewBlockedWord('');
    setNewReplacement('');
    try { await firestoreService.setWordFilters(updated); } catch (err) { console.error('Error saving word filters:', err); }
  };

  const handleRemoveWordFilter = async (index) => {
    const updated = wordFilters.filter((_, i) => i !== index);
    setWordFilters(updated);
    try { await firestoreService.setWordFilters(updated); } catch (err) { console.error('Error saving word filters:', err); }
  };

  const handleDismissReport = (reportId) => {
    setReports(reports.map(r => r.id === reportId ? { ...r, status: 'dismissed' } : r));
    firestoreService.updateReport(reportId, { status: 'dismissed' }).catch(err => console.error('Error dismissing report:', err));
  };

  const handleRemoveContent = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report && report.reportedContentType === 'post' && report.reportedContentId) {
      setPosts(posts.filter(p => p.id !== report.reportedContentId));
      setReports(reports.map(r => r.id === reportId ? { ...r, status: 'resolved', action: 'content_removed' } : r));
      firestoreService.deletePost(report.reportedContentId).catch(err => console.error('Error removing content:', err));
      firestoreService.updateReport(reportId, { status: 'resolved', action: 'content_removed' }).catch(err => console.error('Error updating report:', err));
      showAlert('Content removed successfully', 'Success');
    }
  };

  const handleSuspendUser = (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report && report.reportedUserId) {
      const reason = prompt('Enter suspension reason:', report.reason);
      if (reason) {
        const daysInput = prompt('Enter suspension duration in days (leave empty for permanent):');
        const days = daysInput ? parseInt(daysInput) : null;
        suspendUser(report.reportedUserId, reason, days);
        setReports(reports.map(r => r.id === reportId ? { ...r, status: 'resolved', action: 'user_suspended' } : r));
        firestoreService.updateReport(reportId, { status: 'resolved', action: 'user_suspended' }).catch(err => console.error('Error updating report:', err));
        showAlert('User suspended successfully', 'Success');
      }
    }
  };

  return (
    <div className="flex h-screen">
      <aside className="w-64 bg-gray-900 text-white p-4">
        <div className="flex items-center space-x-3 mb-8">
          <Shield className="w-6 h-6" />
          <h2 className="text-xl font-bold">
            {currentUser.role === 'superadmin' ? 'Super Admin Panel' :
             currentUser.role === 'admin' ? 'Admin Panel' : 'Moderation Panel'}
          </h2>
        </div>

        <nav className="space-y-2">
          {(currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
            <>
              <button onClick={() => setView('admin-dashboard')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-dashboard' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
                <BarChart3 className="w-5 h-5" />
                <span>Dashboard</span>
              </button>
              <button onClick={() => setView('admin-users')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-users' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
                <Users className="w-5 h-5" />
                <span>Users</span>
              </button>
            </>
          )}
          <button onClick={() => setView('admin-moderation')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-moderation' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
            <Flag className="w-5 h-5" />
            <span>Moderation</span>
          </button>
          <button onClick={() => setView('admin-word-moderation')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-word-moderation' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
            <MessageCircle className="w-5 h-5" />
            <span>Word Moderation</span>
          </button>
          <button onClick={() => setView('admin-disputes')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-disputes' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
            <FileText className="w-5 h-5" />
            <span>Disputes</span>
          </button>
          {(currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
            <button onClick={() => setView('admin-app-config')} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg ${view === 'admin-app-config' ? 'bg-[#16a34a]' : 'hover:bg-gray-800'}`}>
              <Settings className="w-5 h-5" />
              <span>App Config</span>
            </button>
          )}
          <button onClick={() => setView('feed')} className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-800 mt-8 border-t border-gray-700 pt-8">
            <Home className="w-5 h-5" />
            <span>Back to App</span>
          </button>
        </nav>
      </aside>

      <main className="flex-1 overflow-y-auto bg-gray-900 p-8">
        {view === 'admin-dashboard' && (currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
          <div>
            <h1 className="text-3xl font-bold text-white mb-8">Dashboard</h1>
            <div className="grid grid-cols-3 gap-6">
              <button
                onClick={() => setView('admin-users')}
                className="bg-black rounded-lg shadow border border-gray-700 p-6 hover:border-[#16a34a] transition-colors cursor-pointer text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Total Users</p>
                    <p className="text-3xl font-bold text-white">{users.length}</p>
                  </div>
                  <Users className="w-12 h-12 text-[#16a34a]" />
                </div>
              </button>
              <div className="bg-black rounded-lg shadow border border-gray-700 p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Total Posts</p>
                    <p className="text-3xl font-bold text-white">{posts.length}</p>
                  </div>
                  <FileText className="w-12 h-12 text-blue-600" />
                </div>
              </div>
              <button
                onClick={() => setView('admin-moderation')}
                className="bg-black rounded-lg shadow border border-gray-700 p-6 hover:border-[#16a34a] transition-colors cursor-pointer text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-white">Pending Reports</p>
                    <p className="text-3xl font-bold text-red-600">{reports.filter(r => r.status === 'pending').length}</p>
                  </div>
                  <AlertCircle className="w-12 h-12 text-red-600" />
                </div>
              </button>
            </div>
          </div>
        )}

        {view === 'admin-users' && (currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
          <div>
            <h1 className="text-3xl font-bold text-white mb-8">User Management</h1>
            <div className="bg-black rounded-lg shadow">
              <table className="w-full">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase">User</th>
                    <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase">Email</th>
                    <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase">Status</th>
                    <th className="px-2 py-1 text-left text-xs font-medium text-white uppercase">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(user => (
                    <tr key={user.id}>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Avatar user={user} size="md" />
                          <div className="ml-4">
                            <button
                              onClick={() => setView(`admin-user-${user.id}`)}
                              className="font-bold text-white hover:text-[#16a34a]"
                            >
                              {user.name}
                            </button>
                            <p className="text-sm text-white">@{user.handle}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-white">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.status === 'active' ? 'bg-slate-700 text-white' :
                          user.status === 'frozen' ? 'bg-yellow-800 text-pure-white' :
                          'bg-red-800 text-pure-white'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-white">{user.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'admin-moderation' && (
          <div>
            <h1 className="text-3xl font-bold text-white mb-8">Content Moderation</h1>
            {reports.filter(r => r.status === 'pending').length === 0 ? (
              <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
                <CheckCircle className="w-16 h-16 text-[#16a34a] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">All Clear!</h3>
                <p className="text-white">No pending reports</p>
              </div>
            ) : (
              <div className="space-y-4">
                {reports.filter(r => r.status === 'pending').map(report => {
                  const reporter = users.find(u => u.id === report.reporterId);
                  const reported = users.find(u => u.id === report.reportedUserId);
                  return (
                    <div key={report.id} className="bg-black rounded-lg shadow border border-gray-700 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-white">{report.reason}</h3>
                          <p className="text-sm text-white">
                            Reported by:{' '}
                            <button
                              onClick={() => setView(`admin-user-${reporter?.id}`)}
                              className="font-bold text-white hover:text-[#16a34a]"
                            >
                              {reporter?.name}
                            </button>
                          </p>
                          <p className="text-sm text-white">
                            Reported user:{' '}
                            <button
                              onClick={() => setView(`admin-user-${reported?.id}`)}
                              className="font-bold text-white hover:text-[#16a34a]"
                            >
                              {reported?.name}
                            </button>
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs ${
                          report.severity === 'high' ? 'bg-red-800 text-pure-white' :
                          report.severity === 'medium' ? 'bg-yellow-800 text-pure-white' :
                          'bg-blue-800 text-pure-white'
                        }`}>
                          {report.severity}
                        </span>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 mb-4">
                        <p className="text-sm font-bold text-white mb-2">Report Details:</p>
                        <p className="text-sm text-white">{report.description || 'No additional details provided'}</p>
                      </div>
                      {report.reportedContentType === 'post' && report.reportedContentId && (() => {
                        const reportedPost = posts.find(p => p.id === report.reportedContentId);
                        if (reportedPost) {
                          const postAuthor = users.find(u => u.id === reportedPost.userId);
                          return (
                            <div className="bg-gray-900 rounded-lg p-4 mb-4 border-l-4 border-red-600">
                              <p className="text-sm font-bold text-white mb-2">Reported Content:</p>
                              <div className="flex items-center space-x-2 mb-2">
                                {postAuthor && (
                                  <>
                                    <Avatar user={postAuthor} size="sm" />
                                    <button
                                      onClick={() => setView(`admin-user-${postAuthor.id}`)}
                                      className="font-bold text-white hover:text-[#16a34a] text-sm"
                                    >
                                      {postAuthor.name}
                                    </button>
                                    <span className="text-xs text-gray-400">@{postAuthor.handle}</span>
                                  </>
                                )}
                              </div>
                              <p className="text-sm text-white">{reportedPost.content}</p>
                              {reportedPost.type && (
                                <span className="inline-block mt-2 px-2 py-1 bg-slate-700 text-white text-xs rounded-full">
                                  {reportedPost.type}
                                </span>
                              )}
                            </div>
                          );
                        }
                        return (
                          <div className="bg-gray-900 rounded-lg p-4 mb-4 border-l-4 border-red-600">
                            <p className="text-sm font-bold text-white mb-2">Reported Content:</p>
                            <p className="text-sm text-gray-400 italic">Content has been deleted</p>
                          </div>
                        );
                      })()}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleDismissReport(report.id)}
                          className="px-2 py-1 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
                        >
                          Dismiss
                        </button>
                        <button
                          onClick={() => handleRemoveContent(report.id)}
                          className="px-2 py-1 bg-yellow-600 text-pure-white rounded-lg hover:bg-yellow-700"
                          disabled={report.reportedContentType !== 'post'}
                        >
                          Remove Content
                        </button>
                        <button
                          onClick={() => handleSuspendUser(report.id)}
                          className="px-2 py-1 bg-red-600 text-pure-white rounded-lg hover:bg-red-700"
                        >
                          Suspend User
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        )}

        {view === 'admin-word-moderation' && (
          <div>
            <h1 className="text-3xl font-bold text-white mb-8">Word Moderation</h1>
            <div className="bg-black rounded-lg shadow border border-gray-700 p-6">
              <p className="text-sm text-gray-400 mb-4">Blocked words will be replaced on display. Original content is preserved in the database.</p>
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="text"
                  value={newBlockedWord}
                  onChange={(e) => setNewBlockedWord(e.target.value)}
                  placeholder="Blocked word"
                  className="flex-1 px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm"
                />
                <input
                  type="text"
                  value={newReplacement}
                  onChange={(e) => setNewReplacement(e.target.value)}
                  placeholder="Replacement (default: ***)"
                  className="flex-1 px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm"
                />
                <button
                  onClick={handleAddWordFilter}
                  className="px-3 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d] text-sm"
                >
                  Add
                </button>
              </div>
              {wordFilters.length === 0 ? (
                <p className="text-sm text-gray-500">No word filters configured.</p>
              ) : (
                <div className="space-y-2">
                  {wordFilters.map((wf, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-900 rounded-lg px-3 py-2">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-red-400 font-mono">{wf.blocked}</span>
                        <span className="text-sm text-gray-500">→</span>
                        <span className="text-sm text-green-400 font-mono">{wf.replacement}</span>
                      </div>
                      <button onClick={() => handleRemoveWordFilter(i)} className="text-gray-500 hover:text-red-500">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'admin-disputes' && (
          <div>
            <h1 className="text-3xl font-bold text-white mb-8">Disputes</h1>
            <div className="flex space-x-2 mb-6">
              <button
                onClick={() => setDisputeTab('pending')}
                className={`px-4 py-2 rounded-lg font-medium ${disputeTab === 'pending' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
              >
                Pending ({disputes.filter(d => d.status === 'pending').length})
              </button>
              <button
                onClick={() => setDisputeTab('resolved')}
                className={`px-4 py-2 rounded-lg font-medium ${disputeTab === 'resolved' ? 'bg-[#16a34a] text-pure-white' : 'bg-gray-800 text-white hover:bg-gray-700'}`}
              >
                Resolved
              </button>
            </div>

            {(() => {
              const filtered = disputes.filter(d => disputeTab === 'pending' ? d.status === 'pending' : d.status !== 'pending');
              if (filtered.length === 0) {
                return (
                  <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
                    <CheckCircle className="w-16 h-16 text-[#16a34a] mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-white mb-2">
                      {disputeTab === 'pending' ? 'No Pending Disputes' : 'No Resolved Disputes'}
                    </h3>
                  </div>
                );
              }
              return (
                <div className="space-y-4">
                  {filtered.map(dispute => (
                    <div key={dispute.id} className="bg-black rounded-lg shadow border border-gray-700 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <p className="text-white font-bold">
                            <button
                              onClick={() => setView(`admin-user-${dispute.userId}`)}
                              className="hover:text-[#16a34a]"
                            >
                              {dispute.userName}
                            </button>
                            <span className="text-gray-400 font-normal ml-2">({dispute.userEmail})</span>
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Submitted: {new Date(dispute.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          dispute.penaltyType === 'banned' ? 'bg-red-800 text-pure-white' : 'bg-yellow-800 text-pure-white'
                        }`}>
                          {dispute.penaltyType}
                        </span>
                      </div>

                      <div className="bg-gray-900 rounded-lg p-4 mb-3">
                        <p className="text-sm font-bold text-gray-400 mb-1">Penalty Reason:</p>
                        <p className="text-sm text-white">{dispute.penaltyReason}</p>
                      </div>

                      <div className="bg-gray-900 rounded-lg p-4 mb-4">
                        <p className="text-sm font-bold text-gray-400 mb-1">User's Dispute:</p>
                        <p className="text-sm text-white">{dispute.reason}</p>
                      </div>

                      {dispute.status === 'pending' && (
                        <div>
                          <textarea
                            value={resolutionNotes[dispute.id] || ''}
                            onChange={(e) => setResolutionNotes({ ...resolutionNotes, [dispute.id]: e.target.value })}
                            placeholder="Resolution notes (optional)..."
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm resize-none h-16 mb-3"
                          />
                          <div className="flex space-x-3">
                            <button
                              onClick={async () => {
                                const notes = resolutionNotes[dispute.id] || '';
                                try {
                                  // Approve: unban/unsuspend the user
                                  if (dispute.penaltyType === 'banned') {
                                    await unbanUser(dispute.userId);
                                  } else {
                                    await unsuspendUser(dispute.userId);
                                  }
                                  // Update dispute
                                  await firestoreService.updateDispute(dispute.id, {
                                    status: 'approved',
                                    resolvedBy: currentUser.id,
                                    resolvedAt: new Date().toISOString(),
                                    resolution: notes || 'Dispute approved',
                                  });
                                  setDisputes(disputes.map(d => d.id === dispute.id ? { ...d, status: 'approved', resolvedBy: currentUser.id, resolvedAt: new Date().toISOString(), resolution: notes || 'Dispute approved' } : d));
                                  // Send notification
                                  await firestoreService.createNotification({
                                    targetUserId: dispute.userId,
                                    type: 'dispute_resolved',
                                    message: `Your dispute has been approved. ${notes ? 'Notes: ' + notes : 'Your account has been restored.'}`,
                                    fromUserId: 'system',
                                  });
                                } catch (err) {
                                  console.error('Error approving dispute:', err);
                                  showAlert('Failed to approve dispute.', 'Error');
                                }
                              }}
                              className="px-4 py-2 bg-green-600 text-pure-white rounded-lg hover:bg-green-700 font-medium"
                            >
                              Approve
                            </button>
                            <button
                              onClick={async () => {
                                const notes = resolutionNotes[dispute.id] || '';
                                try {
                                  await firestoreService.updateDispute(dispute.id, {
                                    status: 'denied',
                                    resolvedBy: currentUser.id,
                                    resolvedAt: new Date().toISOString(),
                                    resolution: notes || 'Dispute denied',
                                  });
                                  setDisputes(disputes.map(d => d.id === dispute.id ? { ...d, status: 'denied', resolvedBy: currentUser.id, resolvedAt: new Date().toISOString(), resolution: notes || 'Dispute denied' } : d));
                                  // Send notification
                                  await firestoreService.createNotification({
                                    targetUserId: dispute.userId,
                                    type: 'dispute_resolved',
                                    message: `Your dispute has been denied. ${notes ? 'Notes: ' + notes : ''}`,
                                    fromUserId: 'system',
                                  });
                                } catch (err) {
                                  console.error('Error denying dispute:', err);
                                  showAlert('Failed to deny dispute.', 'Error');
                                }
                              }}
                              className="px-4 py-2 bg-red-600 text-pure-white rounded-lg hover:bg-red-700 font-medium"
                            >
                              Deny
                            </button>
                          </div>
                        </div>
                      )}

                      {dispute.status !== 'pending' && (
                        <div className={`${dispute.status === 'approved' ? 'bg-green-900/50 border-green-700' : 'bg-red-900/50 border-red-700'} border rounded-lg p-3`}>
                          <p className="text-xs text-gray-400">
                            {dispute.status === 'approved' ? 'Approved' : 'Denied'}
                            {dispute.resolvedAt ? ` on ${new Date(dispute.resolvedAt).toLocaleDateString()}` : ''}
                          </p>
                          {dispute.resolution && <p className="text-sm text-white mt-1">{dispute.resolution}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {view === 'admin-app-config' && (currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
          <AppConfigView />
        )}

        {view.startsWith('admin-user-') && (
          <UserAdminView userId={view.replace('admin-user-', '')} />
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
